import { computed, onBeforeUnmount, readonly, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useTimeoutPoll } from '@vueuse/core'
import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr'
import { useApiConfig } from '~/config/api'
import { useAuthStore } from '~/stores/auth'
import { useWarehouse } from '~/composables/useWarehouse'
import { getAccessToken } from '~/utils/authToken'
import {
  combineWatch, createRefetchGate, hintConcerns, pollEvery, retryDelayMs, sameWatch,
  type LiveHint, type LiveInterest, type LiveTopic, type LiveWatch,
} from '~/utils/liveUpdates'

/**
 * The tablet's one live connection to the API (`/hubs/changes`, task 2496). Rules: the
 * "Live updates" section of CLAUDE.md, and `utils/liveUpdates.ts`.
 *
 * It shows nothing on screen. A screen says what it wants to hear about (`useLiveRefresh`); a hint
 * or a reconnect makes that screen read again through its existing request, and while the
 * connection holds its polling slows to a minute. Without the connection - the hub not reachable,
 * the proxy not set up for it yet, a container with no signal - everything works as before.
 */

type Listener = {
  interest: () => LiveInterest
  onHint: (hint: LiveHint) => void
  onConnected: () => void
}

const live = ref(false)
const listeners = new Set<Listener>()
let connection: HubConnection | null = null
let watching: LiveWatch | null = null
let startTimer: ReturnType<typeof setTimeout> | null = null
let startAttempts = 0
let wanted = false
let initialised = false

/** Worked out once, where there is a Nuxt context (a timer's callback has none). */
let hubUrl = ''

function currentWatch(): LiveWatch {
  const { warehouse } = useWarehouse()
  return combineWatch([...listeners].map(l => l.interest()), warehouse.value?.id ?? null)
}

async function sendWatch(force = false) {
  if (!connection || connection.state !== HubConnectionState.Connected) return
  const next = currentWatch()
  if (!force && sameWatch(watching, next)) return
  watching = next
  try {
    await connection.invoke('Watch', next.topics, next.warehouseId, next.gang)
  } catch {
    watching = null // asked again on the next change or reconnect
  }
}

function connected() {
  live.value = true
  startAttempts = 0
  void sendWatch(true)
  // Hints sent while away are not replayed: every screen reads once now.
  for (const listener of listeners) listener.onConnected()
}

function build(): HubConnection {
  const built = new HubConnectionBuilder()
    .withUrl(hubUrl, {
      // Read on every (re)connect, so a refreshed log-in is the one used.
      accessTokenFactory: () => getAccessToken() ?? '',
      transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
    })
    // Never gives up (the default stops after four tries): see retryDelayMs.
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: context => retryDelayMs(context.previousRetryCount) })
    // Silent: an unreachable hub is an expected state (the screen polls), never a console error.
    .configureLogging(LogLevel.None)
    .build()

  built.on('hint', (hint: LiveHint) => {
    for (const listener of listeners) {
      if (hintConcerns(hint, listener.interest())) listener.onHint(hint)
    }
  })
  built.onreconnecting(() => { live.value = false })
  built.onreconnected(() => connected())
  // Closed for good from the server's side (the log-in ran out) or a reconnect that could not
  // finish: start again on our own schedule while anyone still wants it.
  built.onclose(() => {
    live.value = false
    watching = null
    scheduleStart()
  })
  return built
}

function scheduleStart(now = false) {
  if (!wanted || startTimer !== null) return
  const delay = now ? 0 : retryDelayMs(startAttempts)
  startTimer = setTimeout(() => {
    startTimer = null
    void start()
  }, delay)
}

async function start() {
  if (!wanted) return
  if (!getAccessToken()) return
  connection ??= build()
  if (connection.state !== HubConnectionState.Disconnected) return
  try {
    await connection.start()
    connected()
  } catch {
    startAttempts += 1
    scheduleStart()
  }
}

async function stop() {
  if (startTimer !== null) clearTimeout(startTimer)
  startTimer = null
  live.value = false
  watching = null
  const closing = connection
  connection = null
  if (closing) {
    try { await closing.stop() } catch { /* already gone */ }
  }
}

/** Wanted while someone is logged in and a screen is listening. */
function reconsider() {
  const auth = useAuthStore()
  const shouldRun = auth.isAuthenticated && listeners.size > 0
  if (shouldRun === wanted) {
    if (wanted) void sendWatch()
    return
  }
  wanted = shouldRun
  if (wanted) scheduleStart(true)
  else void stop()
}

function initialise() {
  if (initialised || typeof window === 'undefined') return
  initialised = true
  // The API's origin - baseURL without its `/api` - as the queue works it out (useSyncQueue).
  hubUrl = `${useApiConfig().baseURL.replace(/\/api$/i, '')}/hubs/changes`
  const auth = useAuthStore()
  const { warehouse } = useWarehouse()
  // Log out stops it; a new log-in starts it again with the new token.
  watch(() => auth.isAuthenticated, async (now) => {
    if (!now) { wanted = false; await stop() } else reconsider()
  })
  watch(() => warehouse.value?.id ?? null, () => { void sendWatch() })
}

/** The connection, for the few that need it directly (the plugin, Settings). */
export function useLiveUpdates() {
  initialise()
  return {
    live: readonly(live),
    /** The tablet says its Wi-Fi is back: try now rather than at the end of a back-off. */
    retryNow() {
      if (!wanted || live.value) return
      if (startTimer !== null) { clearTimeout(startTimer); startTimer = null }
      void start()
    },
    listen(listener: Listener): () => void {
      listeners.add(listener)
      reconsider()
      return () => {
        listeners.delete(listener)
        reconsider()
      }
    },
    /** Something a listener's interest depends on changed (its gang): the hub is asked again. */
    rewatch() { void sendWatch() },
  }
}

export type LiveRefreshOptions = {
  topics: LiveTopic[]
  /** The gang this screen shows, or null for every gang. Only read for truck-loads. */
  gang?: () => string | null
  /** The screen's own quiet read. */
  refresh: () => unknown
  /** Today's polling interval, kept as the fallback. */
  baseMs: number
  /** At most one hint-driven read this often. */
  minGapMs?: number
  /** While true, hints wait (a truck being loaded); one read follows when it turns false. */
  paused?: () => boolean
  /** Also poll `refresh` at `pollMs` (default true). Off for a screen that never polled. */
  poll?: boolean
}

/**
 * A screen's part: read again on a hint and on every (re)connect, at most once per `minGapMs`,
 * and poll at `pollMs` - the screen's own interval without the connection, a minute with it.
 */
export function useLiveRefresh(options: LiveRefreshOptions): { live: Readonly<Ref<boolean>>, pollMs: ComputedRef<number> } {
  const liveUpdates = useLiveUpdates()
  const gate = createRefetchGate(options.refresh, options.minGapMs ?? 2_000)
  const paused = () => options.paused?.() ?? false
  const listener: Listener = {
    interest: () => ({ topics: options.topics, gang: options.gang?.() ?? null }),
    onHint: () => gate.poke(paused()),
    onConnected: () => gate.poke(paused()),
  }
  const unlisten = liveUpdates.listen(listener)
  const stopGang = options.gang ? watch(options.gang, () => liveUpdates.rewatch()) : null
  const stopPause = options.paused ? watch(paused, (now) => { if (!now) gate.resume() }) : null

  const pollMs = computed(() => pollEvery(options.baseMs, liveUpdates.live.value))
  // The screen's polling, as before, at the pace the connection allows. useTimeoutPoll waits for
  // each read before the next and stops when the screen is left; a change of pace restarts the
  // wait, so a dropped connection is back to the screen's own interval at once.
  const poller = options.poll === false ? null : useTimeoutPoll(async () => { await options.refresh() }, pollMs)
  const stopPace = poller ? watch(pollMs, () => { poller.pause(); poller.resume() }) : null

  onBeforeUnmount(() => {
    unlisten()
    gate.stop()
    stopGang?.()
    stopPause?.()
    stopPace?.()
  })

  return { live: liveUpdates.live, pollMs }
}
